import {Routes, UrlMatchResult, UrlSegment} from '@angular/router';

import {DASHBOARD_TABS} from '../../dashboard/models/dashboard';
import {FEEDBACK_TABS} from '../../feedbacks/models/feedback';
import {MAP_TABS} from '../../maps/models';
import {RELEASES_TABS} from '../../releases/models/table';
import {TAB_TYPES} from '../../spaces/models/spaces.interface';
import {ChangesGuard} from '../guards/changes.guard';
import {LicenseGuard} from '../guards/license.guard';
import {MetadataDictionariesGuard} from '../guards/metadata-dictionaries.guard';
import {QueryParamsGuard} from '../guards/query-params.guard';
import {twoFARequiredGuard} from '../guards/twofa-required.guard';
import {ROUTES_PATH} from '../models/routes-path.enum';
import {customFieldsResolver} from '../resolvers/custom-fields.resolver';

export const routes: Routes = [
  {
    path: ROUTES_PATH.SPACES,
    canActivate: [QueryParamsGuard('ref', Object.values(TAB_TYPES))],
    title: 'Spaces',
    loadChildren: () => import('../../spaces/spaces.module').then(m => m.SpacesModule)
  },
  {
    path: ROUTES_PATH.RELEASES,
    canActivate: [QueryParamsGuard('ref', Object.values(RELEASES_TABS))],
    title: 'Releases',
    loadChildren: () => import('../../releases/releases.module').then(m => m.ReleasesModule)
  },
  {
    path: ROUTES_PATH.URL_LIBRARY,
    title: 'Url Library',
    canActivate: [QueryParamsGuard('ref', ['active'])],
    canMatch: [LicenseGuard(permissionsService => permissionsService.hasUrlLibraryLicense())],
    loadChildren: () => import('../../url-library/url-library.module').then(m => m.UrlLibraryModule)
  },
  {
    path: ROUTES_PATH.METADATA_DICTIONARIES,
    title: 'Metadata dictionaries',
    children: [],
    canMatch: [MetadataDictionariesGuard],
    pathMatch: 'full'
  },
  {
    path: ROUTES_PATH.REFERENCE_MODEL,
    title: 'Internal Models',
    canMatch: [MetadataDictionariesGuard],
    loadChildren: () =>
      import('../../reference-models/reference-models.module').then(m => m.ReferenceModelsModule)
  },
  {
    path: ROUTES_PATH.SF_ORGS,
    title: 'External Orgs',
    canMatch: [MetadataDictionariesGuard],
    loadChildren: () =>
      import('../../salesforce-orgs/salesforce-orgs.module').then(m => m.SalesforceOrgsModule)
  },
  {
    path: ROUTES_PATH.AI_AGENTS,
    redirectTo: ROUTES_PATH.MAPS,
    pathMatch: 'full'
  },
  {
    path: ROUTES_PATH.MAPS,
    title: 'Diagrams',
    canActivate: [QueryParamsGuard('ref', Object.values(MAP_TABS))],
    loadChildren: () => import('../../maps/maps.module').then(m => m.MapsModule)
  },
  {
    path: ROUTES_PATH.STORIES,
    title: 'Stories',
    resolve: {
      customFields: customFieldsResolver
    },
    canMatch: [ChangesGuard],
    loadChildren: () => import('../../stories/stories.module').then(m => m.StoriesModule)
  },
  {
    path: ROUTES_PATH.REQUIREMENTS,
    title: 'Requirements',
    resolve: {
      customFields: customFieldsResolver
    },
    canMatch: [ChangesGuard],
    loadChildren: () =>
      import('../../requirements/requirements.module').then(m => m.RequirementsModule)
  },
  {
    path: ROUTES_PATH.FEEDBACKS,
    title: 'Feedbacks',
    canActivate: [QueryParamsGuard('ref', Object.values(FEEDBACK_TABS))],
    canMatch: [ChangesGuard],
    loadChildren: () => import('../../feedbacks/feedbacks.module').then(m => m.FeedbacksModule)
  },
  {
    path: ROUTES_PATH.PROFILE,
    title: 'Profile',
    loadChildren: () => import('../../profile/profile.module').then(m => m.ProfileModule)
  },
  {
    path: ROUTES_PATH.CHANGES,
    title: 'Changes',
    children: [],
    canMatch: [ChangesGuard],
    pathMatch: 'full'
  },
  {
    path: ROUTES_PATH.DASHBOARD,
    title: 'Dashboard',
    canActivate: [QueryParamsGuard('ref', Object.values(DASHBOARD_TABS))],
    loadChildren: () => import('../../dashboard/dashboard.module').then(m => m.DashboardModule)
  },
  {
    path: ROUTES_PATH.SETUP_TWO_FA,
    canActivate: [twoFARequiredGuard],
    loadComponent: () =>
      import('../../profile/dialogs/2fa-setup/components/two-fa-shell/two-fa-shell.component').then(
        c => c.TwoFaShellComponent
      )
  },
  {
    path: ROUTES_PATH.USAGE,
    title: 'Usage',
    loadChildren: () => import('../../usage/usage.module').then(m => m.UsageModule)
  },
  {
    path: '**',
    redirectTo: ROUTES_PATH.NOT_FOUND
  }
];

export function CommonRoutingMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (!segments.length) {
    return null;
  }

  const [root] = segments;

  if (['t', ROUTES_PATH.TASKS, ROUTES_PATH.SETUP_TWO_FA, 'demo'].includes(root?.path)) {
    return {consumed: []};
  }

  return null;
}
